import TaskAssignee from "@/components/TaskAssignee";
import React, { useEffect, useRef, useState } from "react";
import { taskDraftChanges, taskNextStep } from "../../lib/taskWorkflow";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Save, History, ListChecks, Plus, Trash2, Check, Archive,
  Play, UserCheck, Inbox, Send, Copy, Link2, MessageSquare,
} from "lucide-react";
import api from "@/lib/api";
import { Card, Spinner } from "@/components/common";
import PaketPruefung from "@/components/PaketPruefung";
import { StatusPill, VisPill, PrioPill, Progress, KundeAmZug, PRIORITIES, fmtDate, inputCls } from "./taskUtils";
import { useI18n } from "@/context/I18nContext";

export default function TaskDetail() {
  const { t } = useI18n();
  const { id } = useParams();
  const nav = useNavigate();
  const [task, setTask] = useState(null);
  const [error, setError] = useState("");
  const [cfg, setCfg] = useState(null);
  const [edit, setEdit] = useState({});
  const [checklist, setChecklist] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const writeLock = useRef(false);

  const changes = taskDraftChanges(task, { ...edit, checklist });
  const dirty = !!task && Object.keys(changes).length > 0;
  useEffect(() => {
    if (!dirty) return;
    const warn = event => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const load = (preserveDraft = false) => { setError(""); return Promise.all([api.get(`/tasks/${id}`), api.get("/tasks/config")]).then(([r, c]) => { setCfg(c.data); setTask(r.data); setEdit({ ...r.data, ...(preserveDraft ? changes : {}) }); setChecklist(preserveDraft && changes.checklist ? changes.checklist : r.data.checklist || []); }).catch(e => (preserveDraft ? setSaveError : setError)(e.response?.data?.detail || t("Aufgaben konnten nicht geladen werden."))); };
  useEffect(() => { load(); }, [id]);

  if (error) return <div role="alert" className="p-5"><p>{error}</p><button className="mt-3 min-h-[44px] underline" onClick={load}>{t("Erneut versuchen")}</button></div>;
  if (!task || !cfg) return <Spinner />;
  const slabel = (k) => t(cfg.status_labels[k] || k);
  const vlabel = (k) => t(cfg.visibility_labels[k] || k);
  const set = (k, v) => setEdit((e) => ({ ...e, [k]: v }));
  const step = taskNextStep(task);
  const leave = () => {
    if (!dirty || window.confirm(t("Ungespeicherte Änderungen verwerfen?"))) nav("/admin/tasks");
  };

  const saveField = async (fields) => {
    if (writeLock.current) return false;
    const payload = { ...changes, ...fields };
    if (!Object.keys(payload).length) return true;
    writeLock.current = true;
    setSaving(true);
    setSaveError("");
    try {
      const result = await api.put(`/tasks/${id}`, { ...payload, approval_revision: task.approval_revision || 0 });
      // The PUT already succeeded. A failed history refresh must not report
      // the actual save as failed or discard its returned state.
      const refreshed = await api.get(`/tasks/${id}`).catch(() => null);
      const data = { ...task, ...(refreshed?.data || result.data) };
      setTask(data); setEdit(data); setChecklist(data.checklist || []);
      toast.success(t("Gespeichert"));
      return true;
    } catch (e) {
      const message = e.response?.data?.detail || t("Konnte nicht speichern.");
      setSaveError(message); toast.error(message); return false;
    }
    finally { writeLock.current = false; setSaving(false); }
  };
  const saveDetails = () => saveField({});

  const duplicate = async () => {
    if (dirty && !await saveField({})) return;
    try { const { data } = await api.post(`/tasks/${id}/duplicate`); toast.success(t("Aufgabe dupliziert")); nav(`/admin/tasks/${data.id}`); }
    catch { toast.error(t("Konnte nicht duplizieren.")); }
  };

  const persistChecklist = (list) => { setChecklist(list); saveField({ checklist: list }); };
  const completeTask = () => {
    if (task.billing_block_complete && !window.confirm(t("Diese Aufgabe ist als \"{label}\" markiert und möglicherweise nicht im Paket enthalten. Bitte zuerst Angebot oder Kulanz bestätigen.\n\nTrotzdem als erledigt markieren?").replace("{label}", task.billing_label))) return;
    saveField({ status: "erledigt" });
  };
  const addItem = () => {
    if (!newItem.trim()) return;
    persistChecklist([...checklist, { id: crypto.randomUUID?.() || String(Date.now()), text: newItem.trim(), done: false }]);
    setNewItem("");
  };
  const toggleItem = (i) => persistChecklist(checklist.map((it, idx) => (idx === i ? { ...it, done: !it.done } : it)));
  const removeItem = (i) => persistChecklist(checklist.filter((_, idx) => idx !== i));
  const progress = { done: checklist.filter((i) => i.done).length, total: checklist.length };
  const primaryAction = () => step.action === "start" ? saveField({ status: "in_arbeit" }) : step.action === "review" ? saveField({ status: "pruefung" }) : completeTask();

  return (
    <div className="fade-in max-w-6xl" data-testid="task-detail">
      <button onClick={leave} data-testid="task-back" className="inline-flex min-h-[44px] items-center gap-2 text-sm text-gray-400 hover:text-white mb-4">
        <ArrowLeft className="w-4 h-4" /> {t("Zurück zu den Aufgaben")}
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-black tracking-tight">{task.title}</h1>
            <StatusPill status={task.status} label={slabel(task.status)} testid="task-detail-status-badge" />
            <VisPill v={task.visibility} label={vlabel(task.visibility)} testid="task-detail-vis-badge" />
            {task.kunde_am_zug && <KundeAmZug testid="task-detail-kundezug" />}
            {task.overdue && <span className="text-[11px] px-2.5 py-1 rounded-full border border-red-500/40 bg-red-500/10 text-red-300 font-bold">{t("Überfällig")}</span>}
          </div>
          <p className="text-sm text-gray-400 mt-1">{task.company_name} · {t("Quelle:")} {task.source} · ID <span className="font-mono text-gray-500">{task.id.slice(0, 8)}</span></p>
        </div>
      </div>

      {!cfg.can_write && <p className="mb-4 text-sm text-amber-200">{t("Nur Lesezugriff")}</p>}
      <Card className="p-4 sm:p-5 mb-5 border-[#A3E635]/25" data-testid="task-next-step">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-gray-400 mb-1">{t("Nächster Schritt")}</p>
            <h2 className="text-lg font-bold">{t(step.label)}</h2>
            <p className="text-sm text-gray-400 mt-1">{t(step.help)}</p>
            <p className="text-sm mt-3 break-words"><span className="text-gray-400">{t("Zuständig")}: </span>{edit.assignee || "—"}<span className="text-gray-400"> · {t("Fällig")}: </span>{fmtDate(edit.due_date)}</p>
          </div>
          {cfg.can_write && step.action && <button disabled={saving} data-testid="td-primary-action" onClick={primaryAction} className="min-h-[44px] w-full sm:w-auto inline-flex justify-center items-center gap-2 bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold px-4 py-2.5 rounded-lg disabled:opacity-50">{t(step.button)}</button>}
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-3">
          <p role="status" data-testid="td-save-state" className={`text-xs ${dirty ? "text-amber-200" : "text-gray-400"}`}>{t(saving ? "Wird gespeichert…" : dirty ? "Ungespeicherte Änderungen" : "Gespeicherter Stand")}</p>
          {cfg.can_write && <button disabled={saving || !dirty} onClick={saveDetails} data-testid="td-save-all" className="min-h-[44px] inline-flex items-center gap-2 border border-white/15 px-4 rounded-lg text-sm font-semibold disabled:opacity-40"><Save className="w-4 h-4" />{t("Änderungen speichern")}</button>}
        </div>
        {saveError && <div role="alert" className="mt-3 text-sm text-red-300"><p>{saveError}</p><p className="mt-1">{t("Ihre Eingaben bleiben erhalten. Prüfen Sie den Hinweis und versuchen Sie es erneut.")}</p><button className="min-h-[44px] underline" onClick={() => { if (!dirty || window.confirm(t("Ungespeicherte Änderungen verwerfen?"))) load(); }}>{t("Gespeicherten Stand neu laden")}</button></div>}
      </Card>
      <fieldset disabled={!cfg.can_write || saving} className="min-w-0 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <h3 className="font-bold mb-4">{t("Aufgaben-Daten")}</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <F t={t("Titel")} full><input value={edit.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="td-title" className={inputCls} /></F>
              <F t={t("Nächste Aktion")} full><input value={edit.next_action || ""} onChange={(e) => set("next_action", e.target.value)} data-testid="td-nextaction" className={inputCls} placeholder={t("z. B. Bilder prüfen")} /></F>
              <F t={t("Beschreibung")} full><textarea value={edit.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="td-desc" rows={3} className={inputCls} /></F>
              <F t={t("Zuständig")}>
                {cfg.can_write ? <TaskAssignee clientId={task.client_id} value={edit.assignee} assigneeId={edit.assignee_id} generic={cfg.assignees} t={t} testId="td-assignee" className={inputCls} onChange={assignment => setEdit(e => ({ ...e, ...assignment }))} /> : <p>{task.assignee}</p>}
              </F>
              <F t={t("Quelle")}>
                <select value={edit.source || "Manuell"} onChange={(e) => set("source", e.target.value)} className={inputCls}>
                  {cfg.sources.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </F>
              <F t={t("Fällig am")}><input type="date" value={(edit.due_date || "").slice(0, 10)} onChange={(e) => set("due_date", e.target.value)} className={inputCls} /></F>
              <F t={t("Geschätzter Aufwand")}><input value={edit.estimated_effort || ""} onChange={(e) => set("estimated_effort", e.target.value)} className={inputCls} placeholder={t("z. B. 2 Std.")} /></F>
              <F t={t("Kundenhinweis (Cockpit sichtbar)")} full><textarea value={edit.customer_hint || ""} onChange={(e) => set("customer_hint", e.target.value)} data-testid="td-customerhint" rows={2} className={inputCls} /></F>
              <F t={t("Interne Notiz (nie für Kunde)")} full><textarea value={edit.internal_note || ""} onChange={(e) => set("internal_note", e.target.value)} data-testid="td-internalnote" rows={2} className={inputCls} /></F>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold flex items-center gap-2"><ListChecks className="w-4 h-4 text-[#A3E635]" /> {t("Checkliste")}</h3>
              <div className="w-40"><Progress done={progress.done} total={progress.total} /></div>
            </div>
            <div className="space-y-2" data-testid="td-checklist">
              {checklist.length === 0 && <p className="text-sm text-gray-500">{t("Noch keine Checklisten-Punkte.")}</p>}
              {checklist.map((it, i) => (
                <div key={it.id || i} className="flex items-center gap-3 group" data-testid={`td-check-${i}`}>
                  <button onClick={() => toggleItem(i)} data-testid={`td-check-toggle-${i}`}
                    className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${it.done ? "bg-[#A3E635] border-[#A3E635]" : "border-white/20 hover:border-white/40"}`}>
                    {it.done && <Check className="w-3.5 h-3.5 text-black" />}
                  </button>
                  <span className={`flex-1 text-sm ${it.done ? "line-through text-gray-500" : "text-gray-200"}`}>{it.text}</span>
                  <button onClick={() => removeItem(i)} data-testid={`td-check-remove-${i}`} className="p-1 rounded text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <input value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addItem(); }}
                data-testid="td-check-input" className={inputCls} placeholder={t("Neuen Punkt hinzufügen und Enter drücken...")} />
              <button onClick={addItem} data-testid="td-check-add" className="shrink-0 inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold px-4 rounded-lg"><Plus className="w-4 h-4" /></button>
            </div>
          </Card>

          {task.customer_feedback && (
            <Card className="p-6 border-[#A3E635]/30">
              <h3 className="font-bold mb-2 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#A3E635]" /> {t("Kunden-Rückmeldung")}</h3>
              <p className="text-sm text-gray-200" data-testid="td-customer-feedback">{task.customer_feedback}</p>
            </Card>
          )}

          <Card className="p-6">
            <h3 className="font-bold mb-4 flex items-center gap-2"><History className="w-4 h-4 text-[#A3E635]" /> {t("Verlauf / Aktivitäten")}</h3>
            {(task.activity || []).length === 0 ? <p className="text-sm text-gray-500">{t("Noch keine Aktivitäten.")}</p> : (
              <div className="space-y-3" data-testid="td-activity">
                {task.activity.map((a) => (
                  <div key={a.id} className="flex gap-3 text-sm border-b border-white/5 pb-3 last:border-0">
                    <div className="w-2 h-2 rounded-full bg-[#A3E635] mt-1.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium">{a.action}</div>
                      {a.changes && <div className="text-xs text-gray-500 mt-0.5">{renderChanges(a.changes)}</div>}
                      <div className="text-[11px] text-gray-600 mt-0.5">{a.actor_name} · {fmtDate(a.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-4 lg:sticky lg:top-6 self-start">
          <Card className="p-5">
            <h3 className="font-bold mb-4">{t("Status & Sichtbarkeit")}</h3>
            {task.visibility === "freigabe" && <p data-testid="td-approval-state" className="mb-4 text-sm text-amber-200">{t(task.approved ? "Vom Kunden freigegeben" : "Kundenfreigabe ausstehend")}{task.approved_at ? ` · ${fmtDate(task.approved_at)}` : ""}</p>}
            <F t={t("Bearbeitungsstatus")}>
              <select value={edit.status} onChange={(e) => set("status", e.target.value)} data-testid="td-status" className={inputCls}>
                {cfg.status_order.map((s) => <option key={s} value={s}>{slabel(s)}</option>)}
              </select>
            </F>
            <div className="mt-3"><F t={t("Sichtbarkeit (RBAC)")}>
              <select value={edit.visibility} onChange={(e) => set("visibility", e.target.value)} data-testid="td-visibility" className={inputCls}>
                {Object.entries(cfg.visibility_labels).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
              </select>
            </F></div>
            <div className="mt-3"><F t={t("Priorität")}>
              <select value={edit.priority} onChange={(e) => set("priority", e.target.value)} data-testid="td-priority" className={inputCls}>
                {PRIORITIES.map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
              </select>
            </F></div>
            {edit.visibility === "intern" && <p className="mt-3 text-[11px] text-gray-500">{t("Nur für KEWAG sichtbar — niemals im Kunden-Cockpit.")}</p>}
            <p className="mt-4 text-xs text-gray-400">{t("Alle Änderungen oben gemeinsam speichern oder den nächsten Schritt ausführen.")}</p>
          </Card>

          <Card className="p-5 space-y-2.5">
            <details data-testid="td-more-actions"><summary className="min-h-[44px] cursor-pointer font-bold">{t("Weitere Aktionen")}</summary><div className="space-y-2.5 mt-2">
            <ActBtn icon={Play} testid="td-act-start" onClick={() => saveField({ status: "in_arbeit" })}>{t("In Arbeit setzen")}</ActBtn>
            <ActBtn icon={UserCheck} testid="td-act-waitclient" onClick={() => saveField({ status: "warten_auf_kunde" })}>{t("Warten auf Kunde")}</ActBtn>
            <ActBtn icon={Inbox} testid="td-act-review" onClick={() => saveField({ status: "pruefung" })}>{t("Zur Prüfung / Freigabe")}</ActBtn>
            <ActBtn icon={Send} testid="td-act-hint" onClick={() => saveField({ visibility: "kundenhinweis" })}>{t("Im Kunden-Cockpit anzeigen")}</ActBtn>
            <ActBtn icon={UserCheck} testid="td-act-request-approval" onClick={() => saveField({ request_approval: true })}>{t(task.approved ? "Neue Freigabe anfordern" : "Freigabe anfordern")}</ActBtn>
            <ActBtn icon={Check} testid="td-act-done" accent disabled={task.visibility === "freigabe" && !task.approved} onClick={completeTask}>{t("Erledigt markieren")}</ActBtn>
            <ActBtn icon={Copy} testid="td-act-duplicate" onClick={duplicate}>{t("Aufgabe duplizieren")}</ActBtn>
            <ActBtn icon={Archive} testid="td-act-archive" onClick={() => saveField({ status: "archiviert" })}>{t("Archivieren")}</ActBtn>
            </div></details>
          </Card>

          {cfg.can_write && <PaketPruefung entityType="task" entity={task} onChanged={() => load(true)} />}

          <Card className="p-5">
            <h3 className="font-bold mb-2">{t("Übersicht")}</h3>
            <div className="space-y-2 text-sm">
              <Row k={t("Kunde / Projekt")} v={task.company_name} />
              <Row k={t("Priorität")} v={<PrioPill p={task.priority} label={t(task.priority_label || task.priority)} />} />
              <Row k={t("Fällig")} v={<span className={task.overdue ? "text-red-300 font-bold" : ""}>{fmtDate(task.due_date)}</span>} />
              <Row k={t("Zuständig")} v={task.assignee || "—"} />
              <Row k={t("Quelle")} v={task.source} />
              {task.estimated_effort && <Row k={t("Aufwand")} v={task.estimated_effort} />}
            </div>
            {(task.source_lead || task.source_support || task.source_invoice || task.related_module) && (
              <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
                <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{t("Verbunden")}</div>
                {task.source_lead && <LinkRow label={t("Anfrage")} text={task.source_lead.subject || task.source_lead.name} onClick={() => nav(`/admin/leads/${task.source_lead.id}`)} testid="td-link-lead" />}
                {task.source_support && <LinkRow label={t("Support")} text={(task.source_support.subject || task.source_support.ticket_no || t("Supportfall"))} />}
                {task.source_invoice && <LinkRow label={t("Rechnung")} text={task.source_invoice.number || t("Rechnung")} />}
                {task.related_module && <LinkRow label={t("Modul")} text={task.related_module.name} />}
              </div>
            )}
          </Card>
        </div>
      </fieldset>
    </div>
  );
}

export function renderChanges(c) {
  try {
    if (typeof c.sichtbarkeit === "string") return `Sichtbarkeit: ${c.sichtbarkeit}`;
    if (typeof c.prioritaet === "string") return `Priorität: ${c.prioritaet}`;
    if (c.status) return `${c.status.von} → ${c.status.zu}`;
    if (c.sichtbarkeit) return `Sichtbarkeit: ${c.sichtbarkeit.von} → ${c.sichtbarkeit.zu}`;
    if (c.prioritaet?.von) return `Priorität: ${c.prioritaet.von} → ${c.prioritaet.zu}`;
    if (c.faelligkeit) return `Fälligkeit: ${c.faelligkeit.von} → ${c.faelligkeit.zu}`;
    if (c.zustaendig) return `Zuständig: ${c.zustaendig.von} → ${c.zustaendig.zu}`;
    if (c.rueckmeldung) return `«${c.rueckmeldung}»`;
    if (c.checkliste) return "Checkliste aktualisiert";
    return "";
  } catch { return ""; }
}

const F = ({ t, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{t}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const ActBtn = ({ icon: Icon, children, onClick, testid, accent, disabled }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
    className={`w-full min-h-[44px] text-left inline-flex items-center gap-2.5 py-2.5 px-3 rounded-lg text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${accent ? "bg-[#A3E635]/10 hover:bg-[#A3E635]/20 text-[#A3E635]" : "bg-white/5 hover:bg-white/10 text-gray-200"}`}>
    <Icon className="w-4 h-4 shrink-0" /> {children}
  </button>
);

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between gap-3"><span className="text-gray-500">{k}</span><span className="text-gray-200 text-right">{v}</span></div>
);

const LinkRow = ({ label, text, onClick, testid }) => (
  <button onClick={onClick} disabled={!onClick} data-testid={testid}
    className={`w-full flex items-center gap-2 text-sm text-left ${onClick ? "text-[#A3E635] hover:underline" : "text-gray-300"}`}>
    <Link2 className="w-3.5 h-3.5 shrink-0" /><span className="text-gray-500 shrink-0">{label}:</span> <span className="truncate">{text}</span>
  </button>
);

